/**
 * The window control shared by every Tina observability surface.
 *
 * Lived inside `home/index.tsx` until a second page appeared with the same
 * `since`/`until` semantics (`/tina/health`). Two copies of a window picker is
 * how two dashboards start disagreeing about what "last 30 days" means.
 */
import { useMemo } from "react";

import dayjs from "dayjs";

import { cn } from "@/lib/utils";

export type PeriodKey = "24h" | "7d" | "30d" | "90d";

export const PERIOD_KEYS: readonly PeriodKey[] = [
  "24h",
  "7d",
  "30d",
  "90d",
] as const;

export const PERIOD_LABEL: Record<PeriodKey, string> = {
  "24h": "24 h",
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
};

export const PERIOD_DAYS: Record<PeriodKey, number> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

/**
 * ISO `since`/`until` for a period key, stable across re-renders.
 *
 * Note the insights endpoints day-bucket both ends inclusively
 * (`day >= since::date AND day <= until::date`), so a "24 h" window is really
 * today plus yesterday.
 */
export function usePeriodRange(period: PeriodKey): {
  since: string;
  until: string;
} {
  return useMemo(() => {
    const until = dayjs();
    const since = until.subtract(PERIOD_DAYS[period], "day");
    return { since: since.toISOString(), until: until.toISOString() };
  }, [period]);
}

export function PeriodChips({
  value,
  onChange,
}: {
  value: PeriodKey;
  onChange: (next: PeriodKey) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border bg-background p-0.5">
      {PERIOD_KEYS.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={cn(
            "px-2.5 py-1 text-xs font-medium rounded-sm transition-colors",
            value === k
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground hover:bg-muted",
          )}
        >
          {PERIOD_LABEL[k]}
        </button>
      ))}
    </div>
  );
}
